import { useState } from "react";
import { Plus, ArrowLeft, FloppyDisk, Play, Trash, PencilSimple, ListBullets } from "@phosphor-icons/react";
import { T } from "../../themes";
import {
  ComicCard, SectionLabel, Btn, Field, Spinner, ExercisePicker
} from "../../workoutHelpers";
import AppHeader from "../../components/AppHeader";

// ── Muscle group classifier (pure, no side effects) ─────────────────────────
function classifyRoutine(exercises = [], library = []) {
  // Keywords ordered so more-specific patterns are checked first
  const muscleMap = {
    chest: ['chest','pec','bench press','bench','fly','push up','pushup','dip'],
    back: ['back','row','lat pull','pulldown','pull-up','pullup','pull up','chin up','chinup','seated row','cable row','barbell row','t-bar'],
    legs: ['squat','leg press','lunge','hamstring','quad','glute','calf','hip thrust','rdl','romanian','bulgarian','leg curl','leg ext'],
    shoulders: ['shoulder','delt','overhead press','ohp','lateral raise','shrug','face pull','arnold','military press'],
    arms: ['bicep','tricep','curl','skullcrusher','skull crusher','pushdown','hammer curl','preacher'],
    core: ['core','ab ','plank','crunch','oblique','woodchop','dead bug','pallof'],
  };
  const counts = { chest:0, back:0, legs:0, shoulders:0, arms:0, core:0 };
  exercises.forEach(ex => {
    const n = exName(ex, library).toLowerCase();
    Object.entries(muscleMap).forEach(([group, keywords]) => {
      if (keywords.some(k => n.includes(k))) counts[group]++;
    });
  });
  const total = exercises.length || 1;
  const legScore = counts.legs / total;
  const pushCount = counts.chest + counts.shoulders;
  const pullCount = counts.back;
  const pushScore = pushCount / total;
  const pullScore = pullCount / total;
  const upperScore = (pushCount + pullCount) / total;

  // Legs-dominant → legs
  if (legScore >= 0.4) return 'legs';
  // Both push + pull present with minimal legs → upper (not full body)
  if (pushScore >= 0.2 && pullScore >= 0.2 && legScore < 0.2) return 'upper';
  // Push-dominant (chest + shoulders) → push
  if (pushScore >= 0.3) return 'push';
  // Pull-dominant (back) → pull
  if (pullScore >= 0.3) return 'pull';
  // Core-dominant → core
  if (counts.core / total >= 0.4) return 'core';
  // Significant upper work with minimal legs → upper
  if (upperScore >= 0.3 && legScore < 0.2) return 'upper';
  // True full body — significant legs + upper mix
  return 'full';
}

// TODO: ROUTINE_TYPE_STYLE uses hardcoded hex — no semantic T token match yet
const ROUTINE_TYPE_STYLE = {
  push:  { headerBg: '#0f2d3d', dot: '#60a5fa', label: 'Upper · Push' },
  pull:  { headerBg: '#1a1a3d', dot: '#a78bfa', label: 'Upper · Pull' },
  upper: { headerBg: '#0f2838', dot: '#38bdf8', label: 'Upper'        },
  legs:  { headerBg: '#0f3020', dot: '#4ade80', label: 'Lower · Legs' },
  full:  { headerBg: '#2a1a00', dot: '#fbbf24', label: 'Full Body'    },
  core:  { headerBg: '#1a0a2a', dot: '#e879f9', label: 'Core'         },
};

// ── Last used label (pure, no side effects) ─────────────────────────────────
// TODO: Pass sessions prop from WorkoutApp to enable real last-used dates
function lastUsedLabel(sessions = [], routineId, createdAt) {
  const matches = sessions
    .filter(s => s.routine_id === routineId)
    .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  if (!matches.length) {
    // Never been used — only flag it if the routine is genuinely old (14+ days)
    if (!createdAt) return null; // no creation date → show nothing
    const ageDays = Math.floor((Date.now() - new Date(createdAt)) / 86400000);
    if (ageDays < 14) return null; // new routine — give user time, show nothing
    return 'Never used';
  }
  const days = Math.floor((Date.now() - new Date(matches[0].started_at)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days/7)} weeks ago`;
  // 30+ days since last use — surface as staleness indicator
  return `Last used ${Math.floor(days/7)} weeks ago`;
}

// Parse "— Wk {n}" suffix from programme-generated routine names
function parseWeek(name) {
  const m = (name || "").match(/—\s*Wk\s*(\d+)\s*$/i);
  return m ? parseInt(m[1], 10) : null;
}
function stripWeek(name) {
  return (name || "").replace(/\s*—\s*Wk\s*\d+\s*$/i, "").trim();
}

// Resolve exercise display name — builder-created routines have ex.name,
// programme-generated ones only have exerciseId
function exName(ex, library) {
  if (ex.name) return ex.name;
  const lib = (library || []).find(l => l.id === ex.exerciseId);
  return lib?.name || ex.exerciseId || "Exercise";
}

export default function RoutinesScreen({routines,library,onSaveRoutine,onDeleteRoutine,onStartRoutine,persona,darkMode,setDarkMode,profile}){
  const [view,setView]=useState("list"); // list | builder
  const [editRoutine,setEditRoutine]=useState(null);
  const [name,setName]=useState("");
  const [exercises,setExercises]=useState([]);
  const [showPicker,setShowPicker]=useState(false);
  const [saving,setSaving]=useState(false);
  const [saveError,setSaveError]=useState("");

  function openBuilder(r){
    if(r){setName(r.name);setExercises((r.exercises||[]).map(ex=>({...ex,sets:[...(ex.sets||[{reps:"",weight:"",rpe:""}])]})));setEditRoutine(r);}
    else{setName("");setExercises([]);setEditRoutine(null);}
    setView("builder");
  }

  // Routine builder: each exercise stores setCount, repMin, repMax, defaultWeight
  // On workout start, sets are expanded from these values
  function addExToRoutine(libEx){
    setExercises(prev=>[...prev,{exerciseId:libEx.id,name:libEx.name,
      setCount:3, repMin:8, repMax:12, defaultWeight:"",
      sets:[{reps:"8-12",weight:"",rpe:""}]}]);
    setShowPicker(false);
  }
  function removeExFromRoutine(i){setExercises(prev=>prev.filter((_,j)=>j!==i));}
  function updateRoutineEx(ei,field,val){
    setExercises(prev=>{
      const a=[...prev];
      a[ei]={...a[ei],[field]:val};
      // Regenerate sets array from setCount/repMin/repMax/defaultWeight
      const sc=parseInt(field==="setCount"?val:a[ei].setCount)||3;
      const rMin=parseInt(field==="repMin"?val:a[ei].repMin)||8;
      const rMax=parseInt(field==="repMax"?val:a[ei].repMax)||12;
      const dw=field==="defaultWeight"?val:a[ei].defaultWeight;
      const repStr=rMin===rMax?String(rMin):`${rMin}-${rMax}`;
      a[ei]={...a[ei],sets:Array.from({length:sc},()=>({reps:repStr,weight:dw,rpe:""}))};
      return a;
    });
  }

  async function handleSave(){
    if(!name.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSaveRoutine({id:editRoutine?.id,name:name.trim(),exercises});
      setSaving(false);
      setView("list");
    } catch(e) {
      if(process.env.NODE_ENV!=="production") console.error("[handleSave]",e);
      setSaving(false);
      setSaveError("Failed to save. Please try again.");
    }
  }

  // ── BUILDER VIEW ──────────────────────────────────────────────────────────
  if(view==="builder"){
    return(
      <div style={{background:T.pageBg,minHeight:"100vh"}}>
        <div style={{background:T.progBannerBg,color:"white",padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:T.shadow}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={()=>setView("list")} style={{background:"none",border:"none",color:"white",cursor:"pointer"}}><ArrowLeft size={18} weight="bold"/></button>
            <span style={{fontSize:20,fontFamily:T.fontDisplay,fontWeight:800,color:"white"}}>{editRoutine?"Edit routine":"New routine"}</span>
          </div>
          <Btn small full={false} onClick={handleSave} disabled={saving||!name.trim()} style={{fontSize:11}}>
            {saving?<Spinner size={12}/>:<FloppyDisk size={13} weight="bold"/>} Save
          </Btn>
        </div>
        <div style={{padding:"14px 16px 100px"}}>
          {saveError&&<p style={{color:T.danger,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold",marginBottom:10,padding:"8px 10px",background:T.surface,border:`1px solid ${T.danger}`,borderRadius:T.radiusXs}}>{saveError}</p>}
          <Field label="Routine Name" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Push Day A"/>
          <SectionLabel>Exercises</SectionLabel>
          {exercises.map((ex,ei)=>(
            <div key={ei} style={{background:T.card,border:`1px solid ${T.cardBorder}`,boxShadow:T.shadowSm,borderRadius:T.radiusSm,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",background:T.tileBg,borderRadius:`${T.radiusSm} ${T.radiusSm} 0 0`}}>
                <span style={{fontSize:16,fontFamily:T.fontDisplay,fontWeight:800,color:"white"}}>{ex.name}</span>
                <button onClick={()=>removeExFromRoutine(ei)} style={{background:"none",border:"none",cursor:"pointer",color:T.tileMuted}}><Trash size={13} weight="bold"/></button>
              </div>
              <div style={{padding:"10px 12px 12px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <p style={{margin:"0 0 4px",fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Sets</p>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <button onClick={()=>updateRoutineEx(ei,"setCount",Math.max(1,(parseInt(ex.setCount)||3)-1))}
                        style={{background:T.surface,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusXs,color:T.text,width:28,height:28,cursor:"pointer",fontSize:14,fontFamily:T.fontSans,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>-</button>
                      <span style={{fontSize:20,fontFamily:T.fontDisplay,fontWeight:800,color:T.text,minWidth:20,textAlign:"center"}}>{ex.setCount||3}</span>
                      <button onClick={()=>updateRoutineEx(ei,"setCount",Math.min(10,(parseInt(ex.setCount)||3)+1))}
                        style={{background:T.surface,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusXs,color:T.text,width:28,height:28,cursor:"pointer",fontSize:14,fontFamily:T.fontSans,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                    </div>
                  </div>
                  <div>
                    <p style={{margin:"0 0 4px",fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Rep Range</p>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <input type="number" value={ex.repMin||8} onChange={e=>updateRoutineEx(ei,"repMin",e.target.value)} min="1" max="50"
                        style={{background:T.surface,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusXs,color:T.text,fontFamily:T.fontSans,fontWeight:"bold",fontSize:12,padding:"4px 6px",outline:"none",width:40,boxSizing:"border-box",textAlign:"center"}}/>
                      <span style={{fontSize:11,fontFamily:T.fontSans,color:T.inkMuted}}>–</span>
                      <input type="number" value={ex.repMax||12} onChange={e=>updateRoutineEx(ei,"repMax",e.target.value)} min="1" max="50"
                        style={{background:T.surface,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusXs,color:T.text,fontFamily:T.fontSans,fontWeight:"bold",fontSize:12,padding:"4px 6px",outline:"none",width:40,boxSizing:"border-box",textAlign:"center"}}/>
                    </div>
                  </div>
                  <div>
                    <p style={{margin:"0 0 4px",fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Default kg</p>
                    <input type="number" value={ex.defaultWeight||""} onChange={e=>updateRoutineEx(ei,"defaultWeight",e.target.value)} placeholder="0"
                      style={{background:T.surface,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusXs,color:T.text,fontFamily:T.fontSans,fontWeight:"bold",fontSize:12,padding:"4px 6px",outline:"none",width:"100%",boxSizing:"border-box"}}/>
                  </div>
                </div>
                <p style={{margin:0,fontSize:9,fontFamily:T.fontSans,color:T.inkMuted}}>
                  {ex.setCount||3} sets &bull; {ex.repMin||8}–{ex.repMax||12} reps{ex.defaultWeight?" &bull; "+ex.defaultWeight+"kg":""}
                </p>
              </div>
            </div>
          ))}
          <Btn outline onClick={()=>setShowPicker(true)} style={{marginBottom:14}}><Plus size={14} weight="bold"/> Add Exercise</Btn>
          {showPicker&&<ExercisePicker library={library} onSelect={addExToRoutine} onClose={()=>setShowPicker(false)}/>}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────

  // Group by week if any routine has a "— Wk N" suffix
  const hasWeeks = routines.some(r => parseWeek(r.name) !== null);
  let groups = [];
  if (hasWeeks) {
    const weekMap = {};
    const noWeek = [];
    routines.forEach(r => {
      const wk = parseWeek(r.name);
      if (wk !== null) {
        if (!weekMap[wk]) weekMap[wk] = [];
        weekMap[wk].push(r);
      } else {
        noWeek.push(r);
      }
    });
    // Sort week numbers ascending
    const sortedWeeks = Object.keys(weekMap).map(Number).sort((a, b) => a - b);
    sortedWeeks.forEach(wk => {
      groups.push({ label: `Week ${wk}`, routines: weekMap[wk] });
    });
    if (noWeek.length > 0) {
      groups.push({ label: "Custom", routines: noWeek });
    }
  } else {
    groups = [{ label: "Your routines", routines }];
  }

  return(
    <div style={{background:T.pageBg,minHeight:"100vh"}}>
      <AppHeader
        title="Routines"
        subtitle={`${routines.length} routine${routines.length!==1?"s":""}`}
        currentApp="lift"
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        username={profile?.username}
      />
      <div style={{padding:"16px 16px 90px"}}>

        {/* New Routine button */}
        <button onClick={()=>openBuilder(null)} style={{
          width:"100%",background:T.orange,border:"none",borderRadius:T.radius,
          padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"center",
          gap:8,cursor:"pointer",marginBottom:16,
        }}>
          <Plus size={16} weight="bold" color="#fff"/>
          <span style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:800,color:"#fff"}}>New routine</span>
        </button>

        {routines.length===0&&(
          <ComicCard>
            <div style={{textAlign:"center",padding:"32px 16px",color:T.inkMuted}}>
              <ListBullets size={48} color={T.inkFaint} style={{margin:"0 auto"}}/>
              <p style={{fontFamily:T.fontDisplay,fontSize:22,fontWeight:800,color:T.inkDark,marginTop:12,marginBottom:6}}>No routines yet</p>
              <p style={{fontSize:12,fontWeight:500,color:T.inkMuted,fontFamily:T.fontSans,margin:0}}>Build a named workout template to reuse anytime</p>
            </div>
          </ComicCard>
        )}

        {routines.length > 0 && groups.map((group, gi) => (
          <div key={gi}>
            {/* Section label */}
            <div style={{
              fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",
              textTransform:"uppercase",marginBottom:10,
              ...(gi > 0 ? {marginTop:20} : {}),
            }}>{group.label}</div>

            {group.routines.map(r => {
              const exList = r.exercises || [];
              const wk = parseWeek(r.name);
              const displayName = wk !== null ? stripWeek(r.name) : r.name;
              const estMins = exList.length * 8;
              const visibleTags = exList.slice(0, 4);
              const extraCount = Math.max(0, exList.length - 4);
              const rType = classifyRoutine(exList, library);
              const rStyle = ROUTINE_TYPE_STYLE[rType] || ROUTINE_TYPE_STYLE.full;
              // sessions not passed to RoutinesScreen yet — pass created_at for age check
              const lastUsed = lastUsedLabel([], r.id, r.created_at);

              return (
                <ComicCard key={r.id} style={{padding:0,marginBottom:8,overflow:"hidden"}}>
                  {/* Classification header strip */}
                  <div style={{
                    background:rStyle.headerBg,padding:"11px 16px",
                    display:"flex",alignItems:"center",justifyContent:"space-between",
                    borderRadius:`${T.radius} ${T.radius} 0 0`,
                  }}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:rStyle.dot,flexShrink:0}}/>
                      <span style={{fontSize:9,fontWeight:700,color:rStyle.dot,textTransform:"uppercase",letterSpacing:"1.2px"}}>{rStyle.label}</span>
                    </div>
                    {lastUsed&&<span style={{fontSize:9,fontWeight:500,color:"rgba(255,255,255,0.55)"}}>{lastUsed}</span>}
                  </div>

                  {/* Body */}
                  <div style={{padding:"14px 16px 0"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:T.fontDisplay,fontSize:22,fontWeight:800,color:T.inkDark,lineHeight:1.1,marginBottom:4}}>{displayName}</div>
                        <div style={{fontSize:11,fontWeight:500,color:T.inkMuted,fontFamily:T.fontSans}}>
                          {exList.length} exercise{exList.length!==1?"s":""} · ~{estMins} mins
                        </div>
                      </div>
                      <div style={{background:T.surfaceLight,borderRadius:T.radiusSm,padding:"5px 10px",fontSize:10,fontWeight:700,color:T.tileMuted,flexShrink:0,marginLeft:8}}>
                        {wk !== null ? `Wk ${wk}` : `${exList.length}`}
                      </div>
                    </div>
                  </div>

                  {/* Tags row */}
                  {exList.length > 0 && (
                    <div style={{padding:"8px 16px 12px",display:"flex",flexWrap:"wrap",gap:6}}>
                      {visibleTags.map((ex,i) => (
                        <span key={i} style={{background:T.surfaceLight,borderRadius:T.radiusPill,padding:"4px 10px",fontSize:10,fontWeight:600,color:T.tileMuted}}>{exName(ex, library)}</span>
                      ))}
                      {extraCount > 0 && (
                        <span style={{background:T.surfaceLight,borderRadius:T.radiusPill,padding:"4px 10px",fontSize:10,fontWeight:600,color:T.tileMuted}}>+{extraCount}</span>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  <div style={{height:1,background:T.cardBorder,margin:"0 16px"}}/>

                  {/* Actions row */}
                  <div style={{padding:"12px 16px",display:"flex",gap:8}}>
                    <button onClick={()=>onStartRoutine(r)} style={{
                      flex:1,background:T.primary,border:"none",borderRadius:12,padding:10,
                      fontSize:12,fontWeight:700,color:"#fff",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:5,
                      fontFamily:T.fontSans,
                    }}>
                      <Play size={12} weight="fill" color="#fff"/> Start
                    </button>
                    <button onClick={()=>openBuilder(r)} style={{
                      background:T.surfaceLight,border:"none",borderRadius:12,padding:"10px 16px",
                      fontSize:12,fontWeight:600,color:T.primary,cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:5,
                      fontFamily:T.fontSans,
                    }}>
                      <PencilSimple size={12} weight="bold" color={T.primary}/> Edit
                    </button>
                    <button onClick={()=>onDeleteRoutine(r.id)} style={{
                      width:38,height:38,background:`${T.danger}18`,border:`1px solid ${T.danger}44`,
                      borderRadius:12,cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                    }}>
                      <Trash size={14} weight="bold" color={T.danger}/>
                    </button>
                  </div>
                </ComicCard>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
